
let isRefreshing = false;
let consecutiveFailures = 0;
let deployedBuild: string | null = null;
let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;

const BASE_POLL_INTERVAL = 90_000;
const MAX_POLL_INTERVAL = 15 * 60_000;

type NewVersionHandler = (info: { reload: () => void }) => void;

const defaultNewVersionHandler: NewVersionHandler = ({ reload }) => reload();

/**
 * Identifies the running build by the ETag of the served index.html, which changes whenever a
 * hashed asset filename inside it changes. Nothing needs stamping at build time, so this reports
 * the artifact actually being served rather than a number someone remembered to bump.
 */
async function fetchBuildId() {
  const response = await fetch('/', { method: 'HEAD', cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`index.html responded ${response.status}`);
  }

  return response.headers.get('etag');
}

export async function checkVersion(onNewVersion: NewVersionHandler = defaultNewVersionHandler) {
  // Skip if tab isn't visible — no point checking while user is away,
  if (document.visibilityState !== 'visible') {
    return;
  }

  if (isRefreshing) {
    return;
  }

  try {
    const latestBuild = await fetchBuildId();
    consecutiveFailures = 0;

    if (!latestBuild) {
      return;
    }

    if (!deployedBuild) {
      deployedBuild = latestBuild;
      return;
    }

    if (latestBuild === deployedBuild) {
      return;
    }

    isRefreshing = true;
    console.log(`New deployment detected: ${deployedBuild} -> ${latestBuild}`);

    onNewVersion({ reload: () => window.location.reload() });
  } catch (error) {
    consecutiveFailures++;
    console.error('Version check failed', error);
  }
}

let hasStartedPolling = false;

export function checkConfigVersion(onNewVersion?: NewVersionHandler) {
  if (hasStartedPolling) return;
  hasStartedPolling = true;

  const scheduleNext = () => {
    // Exponential backoff: 90s → 3min → 6min → ... capped at 15min
    const interval = Math.min(
      BASE_POLL_INTERVAL * Math.pow(2, consecutiveFailures),
      MAX_POLL_INTERVAL
    );

    pollTimeoutId = setTimeout(async () => {
      await checkVersion(onNewVersion);
      scheduleNext();
    }, interval);
  };

  // Run once on tab regaining focus — catches the case where the user
  // came back after the app was idle through a deployment.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkVersion(onNewVersion);
    }
  });

  scheduleNext();
}

const CHUNK_RELOAD_FLAG = 'chunk-reload-attempted';

export function clearChunkReloadFlag() {
  sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
}

export function registerChunkErrorHandler() {
  const handleChunkError = (message: string) => {
    const isChunkError =
      message.includes('ChunkLoadError') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      message.includes('Importing a module script failed');

    if (!isChunkError) return;

    // Guard against infinite reload loops.
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      console.error(
        'Chunk error after reload — not retrying. Likely network or asset issue.'
      );
      return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    console.error('Chunk load error detected. Reloading app.');
    window.location.reload();
  };

  // Catches synchronous script load errors
  window.addEventListener('error', (event) => {
    handleChunkError(event?.message || '');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const message = event?.reason?.message || String(event?.reason || '');
    handleChunkError(message);
  });
}